import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/pages/Login";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { PasswordInput } from "@/components/PasswordInput";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ROLES = [
  ["MASTER_CLIENT", "Client", "Arrange and supervise care for your family"],
  ["SERVICE_PROVIDER", "Healthcare Provider", "Doctor, nurse or therapist offering home care"],
];

export default function Register() {
  const [params] = useSearchParams();
  const requested = params.get("role");
  const extra = requested === "REFERRER" ? [["REFERRER", "Referrer", "Introduce families to verified care"]] : [];
  const options = [...ROLES, ...extra];
  const [mode, setMode] = useState("email");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
    accepted_terms: false,
    role: extra.length ? "REFERRER" : "MASTER_CLIENT",
  });
  const [busy, setBusy] = useState(false);
  const { applySession } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, email: mode === "phone" && !form.email.trim() ? null : form.email.trim() };
      const { data } = await api.post("/api/auth/register", payload);
      applySession(data);
      toast.success("Account created", {
        description: data.dev_otp ? `SMS unavailable — use code ${data.dev_otp}` : "We sent a 6-digit code to your phone",
      });
      navigate("/verify-phone", { replace: true, state: { devOtp: data.dev_otp } });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Create your CARENEST account"
      subtitle="Choose your role — we will guide you through the right verification steps."
      footer={
        <>
          Already registered?{" "}
          <Link to="/login" className="font-semibold text-emerald-800" data-testid="register-to-login-link">
            Log in
          </Link>
        </>
      }
    >
      <GoogleAuthButton label="Sign up with Google" />
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>I am registering as</Label>
          <div className="mt-2 space-y-2">
            {options.map(([value, label, hint]) => (
              <label
                key={value}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${
                  form.role === value ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"
                }`}
              >
                <input
                  type="radio"
                  name="role"
                  value={value}
                  checked={form.role === value}
                  onChange={() => setForm({ ...form, role: value })}
                  className="mt-1 accent-emerald-700"
                  data-testid={`register-role-${value.toLowerCase()}`}
                />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">{label}</span>
                  <span className="block text-xs text-slate-500">{hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label>Sign up with</Label>
          <div className="mt-2 grid grid-cols-2 gap-2 rounded-full bg-slate-100 p-1">
            {[["email", "Email"], ["phone", "Phone number"]].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                className={`rounded-full py-2 text-sm font-semibold transition-colors ${
                  mode === value ? "bg-white text-emerald-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`register-mode-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="full_name">Full name</Label>
          <Input id="full_name" required className="mt-1.5" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="register-name-input" />
        </div>
        <div>
          <Label htmlFor="email">Email address {mode === "phone" && <span className="text-xs font-normal text-slate-500">(optional)</span>}</Label>
          <Input id="email" type="email" required={mode === "email"} className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" />
          {mode === "phone" && <p className="mt-1 text-xs text-slate-500">You can add an email later — you will log in with your phone number.</p>}
        </div>
        <div>
          <Label htmlFor="phone">Phone number</Label>
          <Input id="phone" required placeholder="+2348030000000" className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone-input" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <PasswordInput id="password" required minLength={8} className="mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="register-password-input" />
          <p className="mt-1 text-xs text-slate-500">At least 8 characters.</p>
        </div>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-stone-50 p-3">
          <input
            type="checkbox"
            required
            checked={form.accepted_terms}
            onChange={(e) => setForm({ ...form, accepted_terms: e.target.checked })}
            className="mt-0.5 accent-emerald-700"
            data-testid="register-terms-checkbox"
          />
          <span className="text-xs leading-relaxed text-slate-600">
            I agree to the{" "}
            <Link to="/terms" target="_blank" className="font-semibold text-emerald-800" data-testid="register-terms-link">
              Terms & Conditions
            </Link>
            ,{" "}
            <Link to="/fees" target="_blank" className="font-semibold text-emerald-800" data-testid="register-fees-link">
              Fees & settlement
            </Link>{" "}
            and the{" "}
            <Link to="/privacy" target="_blank" className="font-semibold text-emerald-800" data-testid="register-privacy-link">
              Privacy & NDPA policy
            </Link>
            , including the Arrival = Guaranteed Payout rule.
          </span>
        </label>
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-900 py-6 hover:bg-emerald-800" data-testid="register-submit-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
